import type { GoalView } from '@aj/core';
import { Box, Button, HStack, Pressable, Text, VStack } from '@aj/ui';
import { useState } from 'react';

import { useActions } from '../../data/hooks.js';
import { useT } from '../../providers/I18nProvider.js';

/**
 * Breaking the goal down — an ordered checklist with NO DATES.
 *
 * That is the whole distinction §53.4 draws. "ch.4 is due Tuesday" becomes a
 * lie the moment two days are missed, and rebuilding it is the Sunday
 * optimizer work this product exists to avoid. An ordered list of parts has
 * no wrong state, only a current one: miss a week and the pace recomputes.
 *
 * It is also not a task list. Tasks are things promised for a DAY; parts are
 * the shape of the work, and they sit here on a weekly screen rather than on
 * Today precisely so that planning stays a weekly act.
 */

/** Shown before the rest collapse. A thirty-chapter syllabus rendered in full
 *  is a wall, and the only part that matters is the next one. */
const VISIBLE = 3;

export function PartsList({ goal }: { goal: GoalView }) {
  const t = useT();
  const actions = useActions();
  const [expanded, setExpanded] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [draft, setDraft] = useState('');

  const remaining = goal.partsTotal - goal.partsDone;

  if (goal.partsTotal === 0) {
    return (
      <VStack gap={2} align="start">
        <Text variant="body" tone="muted">
          {t('goal.noParts')}
        </Text>
        {drafting ? (
          <PartDraft
            value={draft}
            onChange={setDraft}
            onSubmit={() => {
              const parts = draft
                .split('\n')
                .map((line) => line.trim())
                .filter(Boolean);
              if (parts.length > 0) actions.setGoalParts(goal.id, parts);
              setDraft('');
              setDrafting(false);
            }}
          />
        ) : (
          <Button size="sm" variant="secondary" icon="plus" onClick={() => setDrafting(true)}>
            {t('goal.addParts')}
          </Button>
        )}
      </VStack>
    );
  }

  return (
    <VStack gap={2}>
      <HStack gap={3} align="baseline" justify="between">
        <Text variant="overline" tone="subtle">
          {t('goal.nextUpTitle')}
        </Text>
        <Text variant="caption" tone="muted" numeric>
          {t('goal.partsOf', { done: goal.partsDone, total: goal.partsTotal })}
        </Text>
      </HStack>

      <VStack as="ul" gap={0} aria-label={t('goal.nextUpTitle')}>
        {(expanded ? goal.parts : goal.parts.slice(0, VISIBLE)).map((part) => (
          <Box as="li" key={part.name}>
          <Pressable
            aria-pressed={part.done}
            onClick={() => {
              if (!part.done) actions.completeGoalPart(goal.id, part.name);
            }}
            className="flex min-h-11 w-full items-center gap-3 rounded-lg px-2 text-left hover:bg-sunken"
          >
            <Text variant="body" tone={part.done ? 'subtle' : 'ink'} className="shrink-0">
              {part.done ? '●' : '○'}
            </Text>
            <Text
              variant="body"
              tone={part.done ? 'subtle' : 'ink'}
              className={part.done ? 'min-w-0 flex-1 truncate line-through' : 'min-w-0 flex-1 truncate'}
            >
              {part.name}
            </Text>
          </Pressable>
          </Box>
        ))}
      </VStack>

      {goal.parts.length > VISIBLE && (
        <Pressable onClick={() => setExpanded((open) => !open)} className="self-start px-2 py-1">
          <Text variant="caption" tone="accent">
            {expanded ? t('goal.showLess') : t('goal.showAll', { count: remaining })}
          </Text>
        </Pressable>
      )}
    </VStack>
  );
}

function PartDraft({
  value,
  onChange,
  onSubmit,
}: {
  value: string;
  onChange: (next: string) => void;
  onSubmit: () => void;
}) {
  const t = useT();

  return (
    <VStack gap={2} className="w-full">
      {/* One per line. A student pasting a chapter list from a syllabus is the
          common case, and making them add rows one at a time for that is the
          kind of friction that ends with nobody using the feature. */}
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={t('goal.partsPlaceholder')}
        aria-label={t('goal.addParts')}
        rows={5}
        className="w-full rounded-lg border border-line bg-sunken p-3 text-base text-ink outline-none placeholder:text-subtle focus-visible:ring-2 focus-visible:ring-ring"
      />
      <Button size="sm" onClick={onSubmit} disabled={value.trim().length === 0}>
        {t('action.save')}
      </Button>
    </VStack>
  );
}
